import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CANCELLATION_REASONS_QUERY } from './cashier-pos.gql';

import { CANCELLATION_SCOPE_FROM_GQL } from '@/shared/graphql/enums';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Label } from '@/shared/ui/label';


interface ReasonsResponse {
  cancellationReasons: {
    items: { id: string; code: string; description: string; scope: string }[];
  };
}

interface ReturnToDoctorModalProps {
  on_close: () => void;
  on_submit: (input: { cancellation_reason_id: string | null; note: string }) => void | Promise<void>;
  submitting: boolean;
}

export function ReturnToDoctorModal({
  on_close,
  on_submit,
  submitting,
}: ReturnToDoctorModalProps): JSX.Element {
  const { t } = useTranslation();
  const { data } = useQuery<ReasonsResponse>(CANCELLATION_REASONS_QUERY, {
    variables: { offset: 0, limit: 100 },
    fetchPolicy: 'cache-first',
  });
  const reasons = (data?.cancellationReasons.items ?? []).filter((reason) => {
    const scope =
      CANCELLATION_SCOPE_FROM_GQL[
        reason.scope as keyof typeof CANCELLATION_SCOPE_FROM_GQL
      ] ?? reason.scope;
    return scope === 'order_return';
  });

  const [reason_id, setReasonId] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const onClickSubmit = async (): Promise<void> => {
    await on_submit({
      cancellation_reason_id: reason_id || null,
      note: note.trim(),
    });
  };

  const can_submit = reason_id.length > 0 || note.trim().length >= 3;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('cashier_pos.return_modal_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="return-reason">{t('cashier_pos.return_reason')}</Label>
            <select
              id="return-reason"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={reason_id}
              onChange={(e) => setReasonId(e.target.value)}
              disabled={submitting}
            >
              <option value="">{t('cashier_pos.return_reason_pick')}</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.description}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="return-note">{t('cashier_pos.return_note')}</Label>
            <textarea
              id="return-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={t('cashier_pos.return_note_placeholder')}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit || submitting}
          >
            {submitting ? t('common.loading') : t('cashier_pos.return_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
